// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, isApiError } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

const verifyOtpTokenRequest = async (
  id: string,
  otp: string,
  codingContext: CodingContext | undefined,
): Promise<void> => {
  if (!id) {
    throw new Error('OTP id must not be empty');
  }
  const request = {
    id: id,
    otp: codingContext?.encryptString(otp),
  };
  await apiClient.post<void>('otp/verify', () => request, codingContext);
};

export const useVerifyOtpTokenMutation = (options?: { onSuccess?: () => void }) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(undefined);
  const mutation = useMutation<void, Error, { otpId: string; token: string }>({
    mutationFn: ({ otpId, token }) => verifyOtpTokenRequest(otpId, token, codingContext),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError(error) {
      if (isApiError(error)) {
        setApiError(error);
      } else {
        setApiError({ error: { code: 'unknown', message: 'Unknown error' } });
      }
    },
  });

  const resetApiError = () => {
    setApiError(undefined);
  };

  return {
    verifyOtpToken: (otpId: string, token: string) => mutation.mutate({ otpId, token }),
    apiError,
    resetApiError,
    ...mutation,
  };
};
